import { Spinner, makeStyles, tokens } from '@fluentui/react-components';
import { useQuery } from '@tanstack/react-query';
import { ResponsiveContainer, LineChart, Line, XAxis, YAxis, Tooltip, CartesianGrid, Legend } from 'recharts';
import { fetchTelemetry } from '../../api/devices.api';
import { useT } from '../../i18n';
import { useIntlFormat } from '../../hooks/useIntlFormat';

/**
 * Fase B — sensores de campo de un módulo (`InvestorModuleDetailPage`).
 * Mismo patrón que `ShipmentTemperatureChart` (F5) pero sobre `targetType=MODULE`
 * y las 3 series reales que trae `SensorReading` para suelo/ambiente:
 * temperatura, humedad ambiente y humedad de suelo. No se inventan series que
 * el modelo no tiene (CE/pH no existen en `SensorReading` — ver
 * `aws-serverless/src/models/device.model.ts`).
 */

const useStyles = makeStyles({
  root:  { display: 'flex', flexDirection: 'column', gap: tokens.spacingVerticalS },
  empty: { padding: '14px 16px', border: `1px dashed ${tokens.colorNeutralStroke2}`, borderRadius: tokens.borderRadiusMedium, color: tokens.colorNeutralForeground3, fontSize: tokens.fontSizeBase200 },
});

interface Props {
  moduleId: string;
}

export function SoilSensorChart({ moduleId }: Props) {
  const s = useStyles();
  const t = useT();
  const { fmtDateTime } = useIntlFormat();

  const { data: readings, isLoading, isError } = useQuery({
    queryKey: ['telemetry', 'MODULE', moduleId],
    queryFn: () => fetchTelemetry('MODULE', moduleId),
  });

  if (isLoading) return <Spinner size="tiny" />;
  if (isError) return <div className={s.empty}>{t.investorModules.telemetryError}</div>;

  const points = (readings ?? []).filter(r =>
    r.temperatureC !== undefined || r.humidityPct !== undefined || r.soilMoisturePct !== undefined,
  );

  if (!points.length) return <div className={s.empty}>{t.investorModules.noTelemetry}</div>;

  const hasTemp = points.some(r => r.temperatureC !== undefined);
  const hasHumidity = points.some(r => r.humidityPct !== undefined);
  const hasSoilMoisture = points.some(r => r.soilMoisturePct !== undefined);

  const chartData = points.map(r => ({
    time: fmtDateTime(r.recordedAt),
    temp: r.temperatureC,
    humidity: r.humidityPct,
    soilMoisture: r.soilMoisturePct,
  }));

  return (
    <div className={s.root}>
      <ResponsiveContainer width="100%" height={220}>
        <LineChart data={chartData}>
          <CartesianGrid strokeDasharray="3 3" />
          <XAxis dataKey="time" tick={{ fontSize: 10 }} minTickGap={40} />
          <YAxis tick={{ fontSize: 10 }} />
          <Tooltip />
          <Legend wrapperStyle={{ fontSize: '11px' }} />
          {hasTemp && <Line type="monotone" dataKey="temp" name={t.investorModules.telemetryTemp} stroke={tokens.colorPaletteRedForeground1} dot={false} strokeWidth={2} connectNulls />}
          {hasHumidity && <Line type="monotone" dataKey="humidity" name={t.investorModules.telemetryHumidity} stroke={tokens.colorPaletteBlueForeground2} dot={false} strokeWidth={2} connectNulls />}
          {hasSoilMoisture && <Line type="monotone" dataKey="soilMoisture" name={t.investorModules.telemetrySoilMoisture} stroke={tokens.colorPaletteGreenForeground1} dot={false} strokeWidth={2} connectNulls />}
        </LineChart>
      </ResponsiveContainer>
    </div>
  );
}
